// ─── DataMap Registry — single source of truth for tooltip text ───────────────
// Re-exports DataMap entries so any component can pull tooltip content from
// the same prose that lives in the Help → Data Dictionary page.
//
// IMPORTANT: tooltip content shown in the UI is the EXACT DataMap text —
// `toFieldTip()` only does structural mapping (DataMap fields → FieldWrap
// sections). It never paraphrases.

import { FIELDS, KPIS, CHARTS } from "../pages/DataMap.jsx";

export { FIELDS, KPIS, CHARTS };

// Map common camelCase tip keys → DataMap entry name. Extend this as we
// migrate inline tooltips. When a key is missing here, the fallback in
// fieldTips.js is used (the explicit hand-written tip).
const KEY_TO_NAME = {
  // ── Marketplaces ──
  payout:           "Payout/Unit",
  pctOfMarket:      "% of Market",
  pctOfTotal:       "% Total",
  payoutDelay:      "Payout Delay",
  reserveHeld:      "Reserve Held",
  reserveRate:      "Reserve Rate (%)",
  avgCogs:          "Avg COGS/unit",
  adCostMkt:        "Ad Cost/unit",
  refundRateMkt:    "Refund Rate",
  deliveryCost:     "Delivery/Unit",
  poolStock:        "Pool Stock (units)",
  poolCost:         "Cost Per Unit (pool)",
  // ── Targets ──
  monthlyTarget:    "Monthly Target",
  actualSold:       "Actual Sold",
  // ── Dashboard / KPIs ──
  cashToCash:       "Cash-to-Cash",
  blendedPayout:    "Blended Payout/Unit",
  runway:           "Runway",
  moneyInInventory: "Money in Inventory",
  stockoutDate:     "Stockout Date",
  reorderDate:      "Reorder Date",
  insufficientDate: "Insufficient Stock Date",
  nextPayments:     "Next Payments",
  healthScore:      "Health Score",
  netThisMonth:     "Net This Month",
  // ── Charts ──
  dashboardChart:   "Cashflow Chart",
  targetsChart:     "Targets Chart",
  plannerTimeline:  "Planner Timeline",
};

// Build a name → entry index across all 3 buckets
function buildIndex() {
  const idx = new Map();
  for (const e of FIELDS) idx.set(e.name?.toLowerCase(), e);
  for (const e of KPIS)   idx.set(e.name?.toLowerCase(), e);
  for (const e of CHARTS) idx.set(e.name?.toLowerCase(), e);
  return idx;
}
let _index = null;
function index() { if (!_index) _index = buildIndex(); return _index; }

/** Look up a DataMap entry by camelCase tip key OR by exact name. */
export function getEntry(idOrName) {
  if (!idOrName) return null;
  const mapped = KEY_TO_NAME[idOrName];
  const key = (mapped || idOrName).toLowerCase();
  return index().get(key) || null;
}

/**
 * Convert a DataMap entry to the FieldWrap tip shape.
 * Output keys: { label, desc, affects, formula, when, whenNot, example }.
 *
 * Mapping is structural only — we DO NOT rewrite prose. Each section's text
 * comes verbatim from the DataMap entry.
 */
export function toFieldTip(entry) {
  if (!entry) return null;
  const what = entry.what || "";
  const calc = entry.calc || "";
  const affects = Array.isArray(entry.affects) ? entry.affects.join("\n• ") : (entry.affects || "");
  const feeds = Array.isArray(entry.feeds) ? entry.feeds.join("\n• ") : "";
  return {
    label: entry.name || "",
    // Sentence-split the WHAT field into bullets so the tooltip never reads as a wall of text.
    desc: what,
    formula: calc,
    affects: affects ? `• ${affects}` : (feeds ? `• ${feeds}` : ""),
    when: entry.withApi || "",
    whenNot: entry.warn || "",
    example: "",
  };
}

/**
 * Convenience: getTipById("payout") → ready-to-render FieldWrap tip object.
 * Returns null when there's no matching DataMap entry — caller should fall
 * back to the explicit fieldTips.js entry in that case.
 */
export function getTipById(idOrName) {
  return toFieldTip(getEntry(idOrName));
}
